import { REHYDRATE } from 'redux-persist/constants';

import { createAction, createSlice } from '@reduxjs/toolkit';
import { OnboardingState } from '@epi-store/onboarding/onboarding.selectors';
import type { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { userApi } from '@epi-api/user';
import { ICustomerData, LunchFundsLoadingValues } from '@epi-models/pages/Home';
import { LoadBenefitPageType } from '@epi-constants/onboardingTypes';

const initialState: OnboardingState = {
  paymentPath: 'topupMultiple',
  loadBenefitType: 'default',
  isSecondStep: false,
  keepOnboardingActive: false
};

const rehydrateAction = createAction<{ onboarding: OnboardingState }, string>(
  REHYDRATE
);

const config: Record<
  LunchFundsLoadingValues,
  Record<'multiple' | 'single', OnboardingState['paymentPath']>
> = {
  DirectLoad: {
    multiple: 'directLoadMultiple',
    single: 'directLoadOnlyLounari'
  },
  TopUpRequest: {
    multiple: 'topupMultiple',
    single: 'topupOnlyLounari'
  }
};

const determinePaymentPath = ({
  lunchFundsLoadingType,
  areThereOtherThanLunchBenefits
}: Pick<
  ICustomerData,
  'lunchFundsLoadingType' | 'areThereOtherThanLunchBenefits'
>): OnboardingState['paymentPath'] => {
  const variant = areThereOtherThanLunchBenefits ? 'multiple' : 'single';

  return lunchFundsLoadingType
    ? config[lunchFundsLoadingType][variant]
    : 'directLoadNoLounari';
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetState: () => initialState,
    setLoadBenefitPageType: (
      state: OnboardingState,
      { payload: loadBenefitType }: PayloadAction<LoadBenefitPageType>
    ) => ({
      ...state,
      loadBenefitType
    }),
    setIsSecondStepDisplayed: (
      state: OnboardingState,
      { payload: isSecondStep }: PayloadAction<boolean>
    ) => ({
      ...state,
      isSecondStep
    }),
    setOnboardingInProgress: (
      state: OnboardingState,
      { payload: keepOnboardingActive }: PayloadAction<boolean>
    ) => ({
      ...state,
      keepOnboardingActive
    })
  },
  extraReducers: builder => {
    builder.addCase(rehydrateAction, (state: OnboardingState, action) => {
      if (action.type === REHYDRATE && action.payload.onboarding) {
        return {
          ...action.payload.onboarding,
          rehydrated: true
        };
      }
      return state;
    });
    builder.addMatcher(
      userApi.endpoints.getDetails.matchFulfilled,
      (state, { payload }: PayloadAction<ICustomerData>) => {
        if (!payload.isEmployeesSet || !payload.isBenefitsSet) {
          state.keepOnboardingActive = true;
        }
        state.paymentPath = determinePaymentPath(payload);
      }
    );
  }
});

export const { reducer, actions: onboardingActions } = onboardingSlice;
